import * as React from 'react';
import { SxProps, Table as MatTable, TableHead, TableRow, TableCell, TableCellProps, TableBody, Theme, Box, tableRowClasses } from '@mui/material';
import { ButtonBlueprint } from '../../factories';
import { archerConstants, keygen } from '../../utils';
import { ToolbarProps, Toolbar } from '../Toolbar';
import { Typography } from '../Typography';

const styles: Record<string, SxProps<Theme>> = {
  tableRoot: {
    overflowY: 'auto'
  },
  body: {
    overflow: 'auto',
    [`& .${tableRowClasses['root']}:hover`]: {
      backgroundColor: archerConstants.archerHover
    }
  },
  titleArea: {
    display: 'flex',
    alignItems: 'center',
    '& p': {
      display: 'block',
      width: 'auto'
    },
    '& div:last-child': {
      marginLeft: 'auto',
      '& > span': {
        marginY: 1,
        marginLeft: 1,
        marginRight: -1
      }
    }
  }
};

export type Padding = 'default' | 'checkbox' | 'dense' | 'none';

export interface ColumnMetadata {
  Property: string;
  Title: string;
}

export interface RowItem {
  [propName: string]: React.FunctionComponent | JSX.Element | string;
}
interface BaseTableProps {
  columnsToRender: ColumnMetadata[];
  isCustomRow?: boolean;
  padding?: Padding;
  align?: TableCellProps['align'];
  hover?: boolean;
  HeaderComponent?: React.FunctionComponent;
  CellComponent?: React.FunctionComponent;
  maxHeight?: number;
  title?: string;
  toolbarItems?: ButtonBlueprint[];
  className?: string;
  sx?: SxProps<Theme>;
}
interface StandardTableProps extends BaseTableProps {
  rows: RowItem[];
  isCustomRow?: false;
}
interface CustomRowTableProps extends BaseTableProps {
  rows: React.FunctionComponent[] | JSX.Element[];
  isCustomRow?: true;
}

export type TableProps = StandardTableProps | CustomRowTableProps;

const createToolbar = (items: ButtonBlueprint[]): any => {
  const toolbar: ToolbarProps['items'] = [
    {
      id: 1,
      position: 'right',
      items
    }
  ];

  return <Toolbar items={toolbar} />;
};

const Table = ({
  columnsToRender,
  rows,
  CellComponent,
  HeaderComponent,
  hover,
  align,
  maxHeight,
  padding,
  title,
  toolbarItems,
  isCustomRow = false,
  ...restProps
}: TableProps) => {
  const { tableRoot, body, titleArea } = styles;
  return (
    <div>
      <Box sx={titleArea}>
        {title && (
          <Typography variant="body2" color="textSecondary">
            {title}
          </Typography>
        )}
        {toolbarItems && toolbarItems.length > 0 && createToolbar(toolbarItems)}
      </Box>
      <Box sx={tableRoot} style={{ maxHeight: `${maxHeight}px` }}>
        <MatTable padding={padding as any} {...restProps}>
          <TableHead>
            <TableRow>
              {columnsToRender.map((column: ColumnMetadata) => {
                return (
                  <TableCell key={column.Title} align={align}>
                    {HeaderComponent ? <HeaderComponent>{column.Title}</HeaderComponent> : <Typography variant="h4">{column.Title}</Typography>}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody sx={body}>
            {Object.values(rows).map((row: RowItem) => {
              return isCustomRow ? (
                row
              ) : (
                <TableRow key={keygen()} hover={hover}>
                  {columnsToRender.map((column: ColumnMetadata) => {
                    const value = row[column.Property];
                    const textValue = typeof value === 'string' ? <Typography variant="body1">{value}</Typography> : value;
                    return (
                      <TableCell key={column.Property} align={align}>
                        {CellComponent ? <CellComponent>{value}</CellComponent> : textValue}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </MatTable>
      </Box>
    </div>
  );
};

Table.displayName = 'Table';

export { Table };
